import {
  ArrowRightIcon,
  CheckCircleIcon,
  CircleNotchIcon,
  WarningCircleIcon,
} from "@phosphor-icons/react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";

import { useAccountCurrency } from "@/client/services/currency/currency";
import { notifyRelationshipChange } from "@/client/services/notification";
import { usePaymentStatus } from "@/client/services/payment/status-payment";
import { useUser } from "@/client/services/user/user";

/** Destination finale : l'espace utilisateur (plus jamais le builder). */
const REDIRECT_PATH = "/client/user-space/resumes";
const COUNTDOWN_SECONDS = 8;
/**
 * Delai avant d'afficher le message rassurant « confirmation en cours ».
 * Le serveur vérifie le fournisseur à chaque appel : le verdict tombe donc
 * généralement en quelques secondes.
 */
const VERIFY_TIMEOUT_SECONDS = 20;

/**
 * Page de retour après paiement (`/client/payment-check/payment`).
 *
 * Verdict clair et brandé (succès / échec / vérification), puis retour dans
 * l'espace utilisateur via compte à rebours ou bouton immédiat.
 *
 * Plus jamais de spinner infini :
 *  - le serveur interroge activement le fournisseur (webhook perdu couvert) ;
 *  - un bouton « Vérifier maintenant » permet de forcer la vérification ;
 *  - en l'absence de paiement en attente, un état explicite propose la sortie.
 */
export const PaymentReturnPage = () => {
  const { format } = useAccountCurrency();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useUser();

  // Identifiant de retour fournisseur si présent, sinon `trxId` mémorisé à
  // l'initiation. Le serveur résout tous les formats (id, référence,
  // référence fournisseur, notify_token).
  const identifier = useMemo(() => {
    const fromUrl =
      searchParams.get("transaction_id") ??
      searchParams.get("merchant_transaction_id") ??
      searchParams.get("trxId") ??
      searchParams.get("reference") ??
      searchParams.get("id");
    return fromUrl ?? window.localStorage.getItem("trxId");
  }, [searchParams]);

  const { data: payment, isFetching, verifyNow } = usePaymentStatus(identifier);

  const [countdown, setCountdown] = useState(COUNTDOWN_SECONDS);
  const [elapsed, setElapsed] = useState(0);

  const isPaid = payment?.status === 1;
  const isFailed = payment?.status === 2;
  const isSettled = isPaid || isFailed;
  const isSlow = !isSettled && elapsed >= VERIFY_TIMEOUT_SECONDS;
  /**
   * Aucun paiement en attente côté serveur ET aucun identifiant exploitable :
   * on ne fait pas patienter l'utilisateur pour rien.
   */
  const isUnknown = !isSettled && !isFetching && !payment && elapsed >= 5;

  const firstName = user?.name?.split(" ")[0];

  // La trace d'initiation est purgée dès que le sort du paiement est tranché.
  useEffect(() => {
    if (isSettled) window.localStorage.removeItem("trxId");
  }, [isSettled]);

  // Changement de relation (achat réussi) : réactive les invitations refusées
  // (SoftPrompt), conformément aux règles web.dev de re-proposition.
  useEffect(() => {
    if (isPaid) notifyRelationshipChange("purchase");
  }, [isPaid]);

  // Horloge : chronomètre de vérification + compte à rebours de redirection.
  useEffect(() => {
    const timer = window.setInterval(() => {
      setElapsed((value) => (isSettled ? value : value + 1));
      if (isSettled) setCountdown((value) => (value <= 1 ? 0 : value - 1));
    }, 1_000);
    return () => window.clearInterval(timer);
  }, [isSettled]);

  useEffect(() => {
    if (isSettled && countdown === 0) navigate(REDIRECT_PATH, { replace: true });
  }, [isSettled, countdown, navigate]);

  const packName = payment?.rate?.name ?? "";
  const amountFcfa = payment?.Amount;

  const title = isPaid
    ? firstName
      ? `Merci ${firstName} `
      : "Merci "
    : isFailed
      ? "Paiement non abouti"
      : isUnknown
        ? "Aucun paiement en attente"
        : isSlow
          ? "Confirmation en cours"
          : "Vérification de votre paiement…";

  const message = isPaid
    ? `Votre paiement a bien été confirmé. Votre pack ${packName} est actif — vos téléchargements et vos crédits sont déjà disponibles dans votre espace.`
    : isFailed
      ? "Votre paiement n'a pas pu être finalisé. Pas d'inquiétude : vous pouvez réessayer en toute tranquillité depuis votre espace, ou nous écrire si un montant a été débité par erreur."
      : isUnknown
        ? "Nous n'avons trouvé aucune transaction à confirmer pour votre compte. Si vous venez de payer, patientez un instant puis cliquez sur « Vérifier maintenant »."
        : isSlow
          ? "L'opérateur n'a pas encore confirmé la transaction. Votre paiement reste suivi par nos serveurs : dès qu'il aboutit, votre pack est activé et vous recevez une notification."
          : "Cela ne prend que quelques instants. Nous confirmons votre transaction auprès du prestataire.";

  const badgeClass = isPaid
    ? "bg-[#3ECF8E]/10 text-[#3ECF8E]"
    : isFailed
      ? "bg-[#E05252]/10 text-[#E05252]"
      : isSlow || isUnknown
        ? "bg-[#F2B705]/10 text-[#F2B705]"
        : "bg-[#472CB3]/10 text-[#472CB3]";

  /** Numéro d'essai affiché pendant la vérification (polling toutes les 3 s). */
  const attemptLabel = Math.max(1, Math.floor(elapsed / 3) + 1);

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#F4F5F7] px-4 py-10">
      <main className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-[0_10px_40px_rgba(26,26,46,0.10)]">
        {/* En-tête de marque */}
        <div className="flex items-center justify-between bg-[#472CB3] px-6 py-5">
          <img src="/logo/mon-cv-pro_logo.svg" alt="Mon CV Pro" className="h-12 w-auto" />
          <span className="text-[11px] font-semibold uppercase tracking-[1.4px] text-[#CDB9FF]">
            Le CV qui vous démarque
          </span>
        </div>

        <div className="flex flex-col items-center px-6 py-8 text-center">
          <div className={`flex h-24 w-24 items-center justify-center rounded-full ${badgeClass}`}>
            {isPaid ? (
              <CheckCircleIcon size={56} weight="fill" />
            ) : isFailed || isSlow || isUnknown ? (
              <WarningCircleIcon size={56} weight="fill" />
            ) : (
              <CircleNotchIcon size={56} weight="bold" className="animate-spin" />
            )}
          </div>

          <h1 className="mt-5 text-2xl font-bold text-[#1A1A2E]">{title}</h1>
          <p className="mt-2 text-sm leading-relaxed text-[#4A4A68]">{message}</p>

          {/* Récapitulatif : pack et montant dès qu'ils sont connus */}
          {payment && !isFailed && !isUnknown && (
            <dl className="mt-6 w-full overflow-hidden rounded-xl border border-[#ECECF1] bg-[#FAFAFC] text-sm">
              {packName && (
                <div className="flex items-center justify-between px-4 py-3">
                  <dt className="text-[#7A7A93]">Pack</dt>
                  <dd className="font-bold text-[#472CB3]">{packName}</dd>
                </div>
              )}
              {typeof amountFcfa === "number" && (
                <div className="flex items-center justify-between border-t border-[#ECECF1] px-4 py-3">
                  <dt className="text-[#7A7A93]">{isPaid ? "Montant payé" : "Montant"}</dt>
                  <dd className="font-bold text-[#1A1A2E]">{format(amountFcfa)}</dd>
                </div>
              )}
            </dl>
          )}

          {/* Vérification en cours : essai visible + action immédiate */}
          {!isSettled && !isUnknown && (
            <p className="mt-4 text-xs text-[#7A7A93]">
              Vérification en cours (essai n°{attemptLabel}) — confirmation automatique dès l'accord
              de l'opérateur.
            </p>
          )}

          {!isSettled && (
            <button
              type="button"
              onClick={() => void verifyNow()}
              disabled={isFetching}
              className="mt-4 inline-flex items-center gap-2 rounded-xl border border-[#472CB3] px-5 py-2.5 text-sm font-bold text-[#472CB3] transition hover:bg-[#472CB3]/5 disabled:opacity-60"
            >
              {isFetching ? (
                <CircleNotchIcon size={16} weight="bold" className="animate-spin" />
              ) : null}
              Vérifier maintenant
            </button>
          )}

          {(isSettled || isSlow || isUnknown) && (
            <button
              type="button"
              onClick={() => navigate(REDIRECT_PATH, { replace: true })}
              className="mt-4 inline-flex items-center gap-2 rounded-xl bg-[#472CB3] px-6 py-3 text-sm font-bold text-white shadow-[0_4px_12px_rgba(71,44,179,0.28)] transition hover:bg-[#3C259B]"
            >
              {isUnknown ? "Aller à mon espace" : "Retour à mon espace"}
              <ArrowRightIcon size={16} weight="bold" />
            </button>
          )}

          {isSettled && (
            <p className="mt-4 text-xs text-[#7A7A93]">
              Redirection vers votre espace dans {countdown} s…
            </p>
          )}

          {isSlow && !isSettled && (
            <p className="mt-3 text-[11px] text-[#9A9AB0]">
              Aucune action de votre part n'est nécessaire : vous serez notifié par e-mail dès la
              confirmation.
            </p>
          )}

          <p className="mt-6 text-[11px] text-[#9A9AB0]">
            Une question sur ce paiement ? Écrivez-nous à{" "}
            <a href="mailto:support@moncvpro.com" className="text-[#472CB3] hover:underline">
              support@moncvpro.com
            </a>
          </p>
        </div>
      </main>
    </div>
  );
};
